import React from 'react';
import { Paper, Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

/**
 * Get sentiment-based styling and icon
 * @param {string} sentiment - Sentiment type
 * @returns {Object} Styling and icon configuration
 */
function getSentimentConfig(sentiment) {
  switch (sentiment) {
    case 'positive':
      return {
        icon: <CheckCircleIcon />,
        color: 'success.main',
        bgColor: 'success.light',
        iconBgColor: 'success.lighter',
        borderColor: 'success.main',
      };
    case 'neutral':
      return {
        icon: <InfoIcon />,
        color: 'info.main',
        bgColor: 'info.light',
        iconBgColor: 'info.lighter',
        borderColor: 'info.main',
      };
    case 'warning':
      return {
        icon: <WarningIcon />,
        color: 'warning.main',
        bgColor: 'warning.light',
        iconBgColor: 'warning.lighter',
        borderColor: 'warning.main',
      };
    case 'negative':
      return {
        icon: <ErrorIcon />,
        color: 'error.main',
        bgColor: 'error.light',
        iconBgColor: 'error.lighter',
        borderColor: 'error.main',
      };
    default:
      return {
        icon: <InfoIcon />,
        color: 'text.secondary',
        bgColor: 'grey.100',
        iconBgColor: 'grey.200',
        borderColor: 'text.secondary',
      };
  }
}

/**
 * @component AIInsightPanel
 * @description Displays AI-generated insight with sentiment-based styling
 * @param {Object} props
 * @param {Object} props.insight - Insight data object
 * @param {string} props.insight.headline - Insight headline
 * @param {string} props.insight.body - Insight body text
 * @param {string} props.insight.sentiment - Sentiment type ('positive', 'neutral', 'warning', 'negative')
 * @param {Object} [props.insight.metric] - Optional metric object
 * @param {string} [props.insight.metric.label] - Metric label
 * @param {string} [props.insight.metric.value] - Metric value
 * @param {string} [props.insight.metric.comparison] - Optional comparison text
 * @returns {JSX.Element}
 */
export function AIInsightPanel({ insight }) {
  const { headline, body, sentiment, metric } = insight;
  const config = getSentimentConfig(sentiment);

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '4px solid',
        borderLeftColor: config.borderColor,
      }}
    >
      {/* Panel Title */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 2,
          color: 'primary.main',
        }}
      >
        <AutoAwesomeIcon sx={{ pr: 1 }}/>
        AI Insights
      </Typography>
      <Box
          sx={{
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
      />

      {/* Header with icon and headline */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: config.iconBgColor,
            color: config.color,
            flexShrink: 0,
          }}
        >
          {config.icon}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.3,
          }}
        >
          {headline}
        </Typography>
      </Box>

      {/* Body text */}
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: metric ? 2 : 0,
          lineHeight: 1.5,
          flexGrow: metric ? 0 : 1,
        }}
      >
        {body}
      </Typography>

      {/* Metric (if present) */}
      {metric && (
        <Box
          sx={{
            pt: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            mb: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              mb: 0.5,
            }}
          >
            {metric.label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: config.color,
              }}
            >
              {metric.value}
            </Typography>
            {metric.comparison && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {metric.comparison}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Footer */}
      <Box
        sx={{
          mt: 1,
          pt: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontSize: '0.7rem',
            fontStyle: 'italic',
          }}
        >
          AI Insights powered by CANai
        </Typography>
      </Box>
    </Paper>
  );
}

